/**
 * Rubber Tire Jam — hand-authored levels.
 *
 * A level is a set of pins on the 2D field plus a list of bands. Each band
 * is a closed elastic loop wrapped around a subset of pins; higher
 * `sortOrder` means the band is drawn on top of (and blocks) lower-order
 * bands. A band is "blocked" while any band sharing a pin with it has a
 * higher sortOrder.
 *
 * Solvability invariant: there must exist a release order such that at
 * every step at least one un-released band has the strictly-highest
 * sortOrder on every pin it touches. The simplest way to guarantee that is
 * to make the sortOrder mapping a topological order — each level below
 * does so by giving the topmost band a unique highest sortOrder and the
 * rest in descending interleavings.
 *
 * Pin coordinates: world units, camera virtual height = 22; field roughly
 * spans x ∈ [-5, 5], y ∈ [-4, 4]. Keep pins inside that box.
 *
 * `color` is a 24-bit hex (not a Color string) because that's what the
 * tangle code uses directly. The handful of values below match
 * `COLOR_HEX` from src/core/colors.ts so the palette stays consistent.
 */

export interface TireJamPin {
  x: number;
  y: number;
}

export interface TireJamBand {
  color: number;
  /** Higher = drawn on top + must be lifted before lower-order siblings. */
  sortOrder: number;
  /** Indices into the level's `pins` array. 2+ pins recommended. */
  pinIndices: number[];
}

export interface TireJamLevel {
  pins: TireJamPin[];
  bands: TireJamBand[];
}

// Colors lifted from COLOR_HEX so designers can eyeball them.
const RED = 0xef4444;
const BLUE = 0x3b82f6;
const YELLOW = 0xfacc15;
const GREEN = 0x22c55e;
const PURPLE = 0xa855f7;
const ORANGE = 0xf97316;

// L1 — original 6-pin layout, 3 bands. Yellow on top, blue, red. Two-step
// obvious solution. (This was the V0 hardcoded level — kept verbatim.)
const L1: TireJamLevel = {
  pins: [
    { x: -2.5, y: 2.0 }, // 0
    { x: 2.5, y: 2.0 }, // 1
    { x: -3.5, y: 0.0 }, // 2
    { x: 0.0, y: 0.0 }, // 3
    { x: 3.5, y: 0.0 }, // 4
    { x: 0.0, y: -2.0 }, // 5
  ],
  bands: [
    { color: RED, sortOrder: 0, pinIndices: [0, 2, 3] },
    { color: BLUE, sortOrder: 1, pinIndices: [1, 3, 4] },
    { color: YELLOW, sortOrder: 2, pinIndices: [2, 3, 4, 5] },
  ],
};

// L2 — 6 pins, 4 bands. Adds a green over yellow that shares pin 3.
// Solve: green → yellow → blue → red.
const L2: TireJamLevel = {
  pins: [
    { x: -2.5, y: 2.5 }, // 0
    { x: 2.5, y: 2.5 }, // 1
    { x: -3.5, y: 0.5 }, // 2
    { x: 0.0, y: 0.5 }, // 3
    { x: 3.5, y: 0.5 }, // 4
    { x: 0.0, y: -2.0 }, // 5
  ],
  bands: [
    { color: RED, sortOrder: 0, pinIndices: [0, 2, 3] },
    { color: BLUE, sortOrder: 1, pinIndices: [1, 3, 4] },
    { color: YELLOW, sortOrder: 2, pinIndices: [2, 3, 4, 5] },
    { color: GREEN, sortOrder: 3, pinIndices: [0, 1, 3] },
  ],
};

// L3 — 7 pins, 5 bands. Adds a purple top band over green, and a row of
// pins to spread the field. Topological cascade: purple → green → yellow →
// blue → red.
const L3: TireJamLevel = {
  pins: [
    { x: -3.0, y: 3.0 }, // 0
    { x: 0.0, y: 3.0 }, // 1
    { x: 3.0, y: 3.0 }, // 2
    { x: -3.5, y: 0.5 }, // 3
    { x: 0.0, y: 0.5 }, // 4
    { x: 3.5, y: 0.5 }, // 5
    { x: 0.0, y: -2.0 }, // 6
  ],
  bands: [
    { color: RED, sortOrder: 0, pinIndices: [0, 3, 4] },
    { color: BLUE, sortOrder: 1, pinIndices: [2, 4, 5] },
    { color: YELLOW, sortOrder: 2, pinIndices: [3, 4, 5, 6] },
    { color: GREEN, sortOrder: 3, pinIndices: [0, 1, 4] },
    { color: PURPLE, sortOrder: 4, pinIndices: [1, 2, 4] },
  ],
};

// L4 — 8 pins, 6 bands. Orange goes on top with widest reach. Solve order:
// orange → purple → green → yellow → blue → red.
const L4: TireJamLevel = {
  pins: [
    { x: -3.0, y: 3.0 }, // 0
    { x: 0.0, y: 3.0 }, // 1
    { x: 3.0, y: 3.0 }, // 2
    { x: -3.5, y: 0.5 }, // 3
    { x: 0.0, y: 0.5 }, // 4
    { x: 3.5, y: 0.5 }, // 5
    { x: -1.5, y: -2.0 }, // 6
    { x: 1.5, y: -2.0 }, // 7
  ],
  bands: [
    { color: RED, sortOrder: 0, pinIndices: [0, 3, 6] },
    { color: BLUE, sortOrder: 1, pinIndices: [2, 5, 7] },
    { color: YELLOW, sortOrder: 2, pinIndices: [3, 4, 5] },
    { color: GREEN, sortOrder: 3, pinIndices: [0, 1, 4] },
    { color: PURPLE, sortOrder: 4, pinIndices: [1, 2, 4] },
    { color: ORANGE, sortOrder: 5, pinIndices: [1, 4, 6, 7] },
  ],
};

// L5 — 9 pins, 7 bands. Top band hits every "tier" so it must come off
// first. Below that the cascade requires the player to peel from the top.
// Pin grid is 3x3-ish for visual clarity.
const L5: TireJamLevel = {
  pins: [
    { x: -3.0, y: 3.0 }, // 0
    { x: 0.0, y: 3.0 }, // 1
    { x: 3.0, y: 3.0 }, // 2
    { x: -3.0, y: 0.5 }, // 3
    { x: 0.0, y: 0.5 }, // 4
    { x: 3.0, y: 0.5 }, // 5
    { x: -3.0, y: -2.0 }, // 6
    { x: 0.0, y: -2.0 }, // 7
    { x: 3.0, y: -2.0 }, // 8
  ],
  bands: [
    { color: RED, sortOrder: 0, pinIndices: [3, 6, 7] }, // bottom-left
    { color: BLUE, sortOrder: 1, pinIndices: [5, 7, 8] }, // bottom-right
    { color: YELLOW, sortOrder: 2, pinIndices: [3, 4, 5] }, // mid row
    { color: GREEN, sortOrder: 3, pinIndices: [0, 3, 4] }, // top-left
    { color: PURPLE, sortOrder: 4, pinIndices: [1, 4, 5, 2] }, // top-right
    { color: ORANGE, sortOrder: 5, pinIndices: [0, 1, 4] }, // top-mid
    { color: RED, sortOrder: 6, pinIndices: [1, 4, 7] }, // spine — comes off first
  ],
};

export const levels: TireJamLevel[] = [L1, L2, L3, L4, L5];
